import { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import {
  AppUpdate,
  appUpdatesOf,
  Client,
  ClientsResponse,
  dateText,
  licenceText,
  UPDATE_APPS,
  UPDATE_PLATFORMS,
  UpdateApp,
  UpdatePlatform,
  VERSION_RE,
} from './api'
import { UNAUTHORISED_TAGS_MESSAGE } from '../../components/UnauthorisedTagsAlert'
import { openableRow } from '../../lib/openableRow'
import styles from './GodMode.module.css'

// What the group edit does to one app on one platform: leave each client's setting as it is, or set the same minimum
// version (blank: none) and force for every client ticked.
interface AppEdit {
  change: boolean
  version: string
  force: boolean
}
type EditKey = `${UpdateApp}.${UpdatePlatform}`
const EDITS = UPDATE_APPS.flatMap((a) => UPDATE_PLATFORMS.map((p) => ({ app: a, platform: p, key: `${a.k}.${p.k}` as EditKey })))
const NO_EDITS = Object.fromEntries(EDITS.map((e) => [e.key, { change: false, version: '', force: false }])) as Record<EditKey, AppEdit>

// One column per app and platform, so clients compare side by side.
const MIN_COLUMNS = EDITS

const appUpdateOf = (c: Client, app: UpdateApp, platform: UpdatePlatform): AppUpdate | undefined => appUpdatesOf(c, app)?.[platform]

// One setting as its column and its filter read it: "20261029A forced", or "Not set".
function minText(u: AppUpdate | undefined) {
  if (!u?.min_version) return 'Not set'
  return `${u.min_version}${u.force ? ' forced' : ''}`
}

const behindOf = (c: Client) =>
  MIN_COLUMNS.reduce((n, { app, platform }) => {
    const u = appUpdateOf(c, app.k, platform.k)
    return n + (u?.min_version ? u.behind : 0)
  }, 0)

// Every client this staff member supports, by name. A row opens the client;
// the filters narrow by name and by each column with a short list of values,
// each app minimum on each platform among them, so clients compare side by side.
// Ticking rows sets the oldest Crew and Field build their phones should be on,
// for all of them at once (PUT /api/god-mode/app-updates).
export function ClientsPage() {
  const { user } = useAuth()
  const [data, setData] = useState<ClientsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [product, setProduct] = useState('')
  const [licence, setLicence] = useState('')
  const [zone, setZone] = useState('')
  const [mins, setMins] = useState<Partial<Record<EditKey, string>>>({})
  const [behind, setBehind] = useState('')
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  const [edits, setEdits] = useState<Record<EditKey, AppEdit>>(NO_EDITS)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [loads, setLoads] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    let live = true
    apiJson<ClientsResponse>(user, '/api/god-mode/clients')
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [user, loads])

  const clients = data?.clients || []
  const breaching = clients.filter((c) => c.unauthorised_tags > 0)
  const productOf = (c: (typeof clients)[number]) => c.product_label || 'Not set'
  const licenceOf = (c: (typeof clients)[number]) => (c.product === 'packright' ? licenceText(c.licence) : '—')
  const zoneOf = (c: (typeof clients)[number]) => c.timezone || 'Not set'
  const values = (of: (c: (typeof clients)[number]) => string) => [...new Set(clients.map(of))].sort()
  const q = search.trim().toLowerCase()
  const shown = clients.filter(
    (c) =>
      (!q || c.name.toLowerCase().includes(q)) &&
      (!product || productOf(c) === product) &&
      (!licence || licenceOf(c) === licence) &&
      (!zone || zoneOf(c) === zone) &&
      MIN_COLUMNS.every(({ app, platform, key }) => !mins[key] || minText(appUpdateOf(c, app.k, platform.k)) === mins[key]) &&
      (!behind || (behind === 'Some behind') === behindOf(c) > 0),
  )
  const shownTicked = shown.filter((c) => ticked.has(c.id))
  const allTicked = shown.length > 0 && shownTicked.length === shown.length
  const tick = (id: string, on: boolean) =>
    setTicked((t) => {
      const next = new Set(t)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  const tickShown = (on: boolean) =>
    setTicked((t) => {
      const next = new Set(t)
      for (const c of shown) {
        if (on) next.add(c.id)
        else next.delete(c.id)
      }
      return next
    })
  const edit = (key: EditKey, change: Partial<AppEdit>) => setEdits((e) => ({ ...e, [key]: { ...e[key], ...change } }))
  // Set starts from the newest version seen on that platform at any client, the usual next minimum.
  const startEdit = (app: UpdateApp, platform: UpdatePlatform, on: boolean) => {
    const key: EditKey = `${app}.${platform}`
    const newest = data?.newest?.[app]?.[platform]
    edit(key, on && !edits[key].version && newest ? { change: on, version: newest } : { change: on })
  }
  const editProblem = (key: EditKey) => {
    const e = edits[key]
    if (!e.change) return null
    const v = e.version.trim().toUpperCase()
    if (v && !VERSION_RE.test(v)) return 'a version reads like 20261029A: the day, then a letter'
    return null
  }
  const changing = EDITS.filter((e) => edits[e.key].change)
  const problem = EDITS.map((e) => {
    const p = editProblem(e.key)
    return p && `${e.app.label} ${e.platform.label}: ${p}`
  }).find(Boolean) || null
  async function applyEdits() {
    const apps: Partial<Record<UpdateApp, Partial<Record<UpdatePlatform, { min_version: string | null; force: boolean }>>>> = {}
    for (const e of changing) {
      const v = edits[e.key].version.trim().toUpperCase()
      apps[e.app.k] = { ...apps[e.app.k], [e.platform.k]: { min_version: v || null, force: !!v && edits[e.key].force } }
    }
    setSaving(true)
    setSaveError(null)
    setSaved(null)
    try {
      await apiJson(user, '/api/god-mode/app-updates', { method: 'PUT', body: { client_ids: shownTicked.map((c) => c.id), apps } })
      setSaved(`Saved for ${shownTicked.length} client${shownTicked.length === 1 ? '' : 's'}.`)
      setEdits(NO_EDITS)
      setLoads((n) => n + 1)
    } catch (e: any) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const filter = (label: string, value: string, set: (v: string) => void, of: (c: (typeof clients)[number]) => string, options = values(of)) => (
    <label className={styles.field}>
      {label}
      <select value={value} onChange={(e) => set(e.target.value)}>
        <option value="">All</option>
        {options.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
    </label>
  )

  return (
    <>
      <PageHeader
        title="Clients"
        actions={
          <Link to="/god-mode/new" className={styles.primary} style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
            New client
          </Link>
        }
      />
      <PageBody>
        <div className={styles.stack}>
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {breaching.length > 0 && (
            <div className={styles.error} role="alert" data-testid="unauthorised-tags">
              <strong>{UNAUTHORISED_TAGS_MESSAGE}</strong>
              <br />
              {breaching.map((c) => `${c.name} (${c.unauthorised_tags})`).join(', ')}. <Link to="/god-mode/tags">See shared tags</Link>
            </div>
          )}
          {data && data.clients.length > 0 && (
            <div className={styles.inline}>
              <label className={styles.field}>
                Search
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
              </label>
              {filter('Product', product, setProduct, productOf)}
              {filter('Licence', licence, setLicence, licenceOf)}
              {filter('Time zone', zone, setZone, zoneOf)}
              {MIN_COLUMNS.map(({ app, platform, key }) => (
                <Fragment key={key}>
                  {filter(`${app.label} ${platform.label} min`, mins[key] || '', (v) => setMins((m) => ({ ...m, [key]: v })), (c) =>
                    minText(appUpdateOf(c, app.k, platform.k)),
                  )}
                </Fragment>
              ))}
              {filter('Phones behind', behind, setBehind, () => '', ['Some behind', 'None behind'])}
            </div>
          )}
          {shownTicked.length > 0 && (
            <div className={styles.card} data-testid="app-updates-edit">
              <h2>
                App versions for {shownTicked.length} client{shownTicked.length === 1 ? '' : 's'}
              </h2>
              <p className={styles.hint}>
                A phone on an older version is asked to update and may do it later. Forced, it can&apos;t be used until
                it has updated; lowering the version or turning force off lets it back in at its next contact. A version
                is the day of the build and a letter, 20261029A, the same on iPhone and Android; any build from before
                versions is older. One that doesn&apos;t say its build (Crew 1.0.0 (6)) is never asked. iPhone and
                Android are set apart, so a fix that is only in one store yet, or a bug on one platform only, asks that
                platform alone. Set starts from the newest version seen there.
              </p>
              {EDITS.map(({ app: a, platform: p, key }) => {
                const name = `${a.label} ${p.label}`
                const e = edits[key]
                return (
                  <div key={key} className={styles.inline}>
                    <label className={styles.field}>
                      {name}
                      <select value={e.change ? 'set' : 'keep'} onChange={(ev) => startEdit(a.k, p.k, ev.target.value === 'set')}>
                        <option value="keep">Leave as is</option>
                        <option value="set">Set</option>
                      </select>
                    </label>
                    {e.change && (
                      <>
                        <label className={styles.field}>
                          {name} minimum version
                          <input value={e.version} placeholder="None, or 20261029A" onChange={(ev) => edit(key, { version: ev.target.value })} />
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={!!e.version.trim() && e.force}
                            disabled={!e.version.trim()}
                            onChange={(ev) => edit(key, { force: ev.target.checked })}
                          />{' '}
                          Force {name} update
                        </label>
                      </>
                    )}
                  </div>
                )
              })}
              {problem && <p className={styles.error}>{problem}</p>}
              {saveError && <p className={styles.error}>{saveError}</p>}
              <div className={styles.inline}>
                <button type="button" className={styles.primary} disabled={saving || !changing.length || !!problem} onClick={applyEdits}>
                  {saving ? 'Saving…' : 'Apply'}
                </button>
                <button type="button" className={styles.secondary} onClick={() => setTicked(new Set())}>
                  Clear selection
                </button>
              </div>
            </div>
          )}
          {saved && <p className={styles.saved}>{saved}</p>}
          {data && (
            <div className={styles.tableCard}>
              {data.clients.length === 0 ? (
                <div className={styles.empty}>
                  No clients yet. <Link to="/god-mode/new">Create the first one</Link>.
                </div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" aria-label="Tick every client shown" checked={allTicked} onChange={(e) => tickShown(e.target.checked)} />
                      </th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Licence</th>
                      <th>Time zone</th>
                      <th>People</th>
                      <th>Sign-ins</th>
                      <th title="Scanned tags another client owns">Unauthorised tags</th>
                      {MIN_COLUMNS.map(({ app, platform, key }) => (
                        <th key={key} title={`The oldest ${app.label} version this client's ${platform.label} phones should be on, and the newest seen there`}>
                          {app.label} {platform.label}
                        </th>
                      ))}
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.length === 0 && (
                      <tr>
                        <td colSpan={9 + MIN_COLUMNS.length}>No client matches these filters.</td>
                      </tr>
                    )}
                    {shown.map((c) => (
                      <tr
                        key={c.id}
                        className={styles.rowLink}
                        {...openableRow(() => navigate(`/god-mode/clients/${c.id}`))}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('a, input, label')) navigate(`/god-mode/clients/${c.id}`)
                        }}
                      >
                        <td>
                          <input type="checkbox" aria-label={`Tick ${c.name}`} checked={ticked.has(c.id)} onChange={(e) => tick(c.id, e.target.checked)} />
                        </td>
                        <td>
                          <Link to={`/god-mode/clients/${c.id}`}>{c.name}</Link>
                        </td>
                        <td>{productOf(c)}</td>
                        <td>{licenceOf(c)}</td>
                        <td>{zoneOf(c)}</td>
                        <td>{c.people}</td>
                        <td title={`${c.signed_in} of ${c.sign_ins} have signed in`}>
                          {c.signed_in} / {c.sign_ins}
                        </td>
                        <td>{c.unauthorised_tags > 0 ? <Link to="/god-mode/tags" className={styles.breach}>{c.unauthorised_tags}</Link> : '—'}</td>
                        {MIN_COLUMNS.map(({ app, platform, key }) => {
                          const u = appUpdateOf(c, app.k, platform.k)
                          const late = u?.min_version ? u.behind : 0
                          return (
                            <td key={key} data-testid={`${app.k}-${platform.k}-min-${c.id}`}>
                              {u?.min_version ? minText(u) : '—'}
                              {(u?.newest || late > 0) && (
                                <div className={styles.hint}>
                                  {[u?.newest && `newest ${u.newest}`, late > 0 && `${late} behind`].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                          )
                        })}
                        <td>{dateText(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <p className={styles.lead}>People and sign-ins leave out Dataphyll's own.</p>
        </div>
      </PageBody>
    </>
  )
}
